import React from 'react';
import { Box, Button, Typography } from '@mui/material';

/**
 * App-wide error boundary. Without this, any render-time throw blanks the entire
 * admin app to a white screen with no recovery path. This catches the throw,
 * shows a friendly fallback, and lets the admin recover without losing the tab.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Surface the error for debugging; replace with a logging service in prod.
    console.error('Admin app crashed:', error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    // When used as a route-level boundary, `resetKey` is the current pathname.
    // A navigation (key change) after an error clears the fallback so one broken
    // page no longer traps the whole session behind a manual reload.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  handleReload = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  handleGoBack = () => {
    this.setState({ hasError: false });
    if (window.history.length > 1) window.history.back();
    else window.location.assign('/dashboard');
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            px: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Something went wrong
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 480 }}>
            An unexpected error occurred while rendering this page. You can try reloading.
            If the problem persists, please contact support.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button variant="contained" onClick={this.handleReload}>
              Reload
            </Button>
            <Button variant="outlined" onClick={this.handleGoBack}>
              Go back
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
